import React from 'react'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { CONTENT_LANGUAGE_FAMILIES } from '@/lib/contentLanguages'

interface LanguagesPickerProps {
  /** The marked languages: families (`pt`) or variants (`pt-BR`). Empty = for everyone. */
  value: string[]
  onChange: (value: string[]) => void
  disabled?: boolean
  /** Why the field cannot take a language here (the home section, its first space). */
  lockedHint?: string | null
  className?: string
}

interface Option {
  code: string
  /** The family it belongs to (its own code for a family). */
  family: string
  isFamily: boolean
  label: string
  /** What a search matches: the label, the language in itself, the country and the code. */
  haystack: string
}

const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase()
const fold = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
const capitalize = (text: string, locale: string) => text.charAt(0).toLocaleUpperCase(locale) + text.slice(1)

/** The options in the interface language: "Português (todos)", "Português (Brasil)", "Espanhol (México)". */
const useOptions = (): Option[] => {
  const { t, i18n } = useTranslation(['common'])
  const locale = i18n.language || 'en-US'
  return React.useMemo(() => {
    const languageName = (code: string) => {
      try {
        return capitalize(new Intl.DisplayNames([locale], { type: 'language' }).of(code) ?? code, locale)
      } catch {
        return code
      }
    }
    const regionName = (region: string) => {
      try {
        return new Intl.DisplayNames([locale], { type: 'region' }).of(region) ?? region
      } catch {
        return region
      }
    }
    return CONTENT_LANGUAGE_FAMILIES.flatMap((family) => {
      const name = languageName(family.code)
      const familyOption: Option = {
        code: family.code,
        family: family.code,
        isFamily: true,
        label: `${name} (${t('common:contentLanguages.allVariants')})`,
        haystack: fold([name, family.name, family.code].join(' '))
      }
      const variants = family.variants.map((variant): Option => {
        const region = regionName(variant.code.split('-')[1] ?? '')
        return {
          code: variant.code,
          family: family.code,
          isFamily: false,
          label: `${name} (${region})`,
          haystack: fold([name, region, variant.language, variant.country, variant.code].join(' '))
        }
      })
      return [familyOption, ...variants]
    })
  }, [locale, t])
}

/**
 * "Restringir por idioma" (F-252): off, the section or space is for everyone
 * (the default; switching it off clears the list). On, a tag input: type to
 * search a language, Enter or a click adds it, Backspace or the × removes it.
 * A family ("Português (todos)") covers its variants; a variant only itself.
 */
export function LanguagesPicker({ value, onChange, disabled, lockedHint, className }: LanguagesPickerProps) {
  const { t } = useTranslation(['common'])
  const options = useOptions()
  const isDisabled = disabled || Boolean(lockedHint)
  const baseId = React.useId()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const listRef = React.useRef<HTMLUListElement>(null)

  /* On by hand or because something is marked (an edit that loads its list after opening). */
  const [restricted, setRestricted] = React.useState(value.length > 0)
  React.useEffect(() => { if (value.length > 0) setRestricted(true) }, [value.length])
  const isOn = restricted && !lockedHint

  const [query, setQuery] = React.useState('')
  const [open, setOpen] = React.useState(false)
  const [active, setActive] = React.useState(0)

  const optionOf = (code: string) => options.find((option) => same(option.code, code))

  const suggestions = React.useMemo(() => {
    const words = fold(query).split(/\s+/).filter(Boolean)
    return options.filter((option) => {
      if (value.some((item) => same(item, option.code))) return false
      /* A variant whose family is marked is already in. */
      if (!option.isFamily && value.some((item) => same(item, option.family))) return false
      return words.every((word) => option.haystack.includes(word))
    })
  }, [options, query, value])

  React.useEffect(() => { setActive(0) }, [query, open])
  React.useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [active])

  const add = (option: Option) => {
    const next = option.isFamily
      /* The family covers its variants: they leave the list instead of doubling it. */
      ? [...value.filter((item) => optionOf(item)?.family !== option.code), option.code]
      : [...value, option.code]
    onChange(next)
    setQuery('')
    inputRef.current?.focus()
  }

  const remove = (code: string) => {
    onChange(value.filter((item) => !same(item, code)))
    inputRef.current?.focus()
  }

  const toggle = (on: boolean) => {
    setRestricted(on)
    if (!on) {
      onChange([])
      setQuery('')
      setOpen(false)
    } else {
      window.setTimeout(() => { inputRef.current?.focus(); setOpen(true) }, 0)
    }
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setOpen(true)
      setActive((index) => Math.min(index + 1, Math.max(0, suggestions.length - 1)))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((index) => Math.max(index - 1, 0))
    } else if (event.key === 'Enter') {
      /* Never submits the form from here. */
      event.preventDefault()
      const pick = suggestions[active]
      if (open && pick) add(pick)
      else setOpen(true)
    } else if (event.key === 'Backspace' && !query && value.length > 0) {
      onChange(value.slice(0, -1))
    }
  }

  /* Escape with the list open closes the list, not the dialog around it. The dialog listens
     on the document in the capture phase, so this listens on the window, one step earlier. */
  React.useEffect(() => {
    if (!open) return
    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.target !== inputRef.current) return
      event.stopPropagation()
      event.preventDefault()
      setOpen(false)
    }
    window.addEventListener('keydown', onEscape, true)
    return () => window.removeEventListener('keydown', onEscape, true)
  }, [open])

  const listId = `${baseId}-list`
  const showList = isOn && open && !isDisabled

  return (
    <div className={cn('space-y-2', className)} data-languages-picker data-restricted={isOn ? 'true' : 'false'}>
      <div className="flex items-center gap-3">
        <Switch
          id={`${baseId}-toggle`}
          checked={isOn}
          onCheckedChange={toggle}
          disabled={isDisabled}
          data-languages-toggle
        />
        <label htmlFor={`${baseId}-toggle`} className={cn('text-sm font-medium', isDisabled ? 'text-muted-foreground' : 'cursor-pointer')}>
          {t('common:contentLanguages.restrict')}
        </label>
      </div>

      {lockedHint && <p className="text-xs text-amber-700" data-languages-locked>{lockedHint}</p>}

      {isOn && (
        <>
          <div className="relative">
            <div
              className={cn(
                'flex min-h-[42px] flex-wrap items-center gap-1.5 rounded-lg border bg-surface p-1.5',
                'focus-within:border-primary focus-within:ring-2 focus-within:ring-primary-200',
                isDisabled && 'opacity-50'
              )}
              onClick={() => { if (!isDisabled) { inputRef.current?.focus(); setOpen(true) } }}
            >
              {value.map((code) => {
                const label = optionOf(code)?.label ?? code
                return (
                  <span key={code} className="flex items-center gap-1 rounded-md bg-primary-50 px-2 py-1 text-sm text-primary" data-language-chip={code}>
                    {label}
                    {!isDisabled && (
                      <button
                        type="button"
                        onClick={(event) => { event.stopPropagation(); remove(code) }}
                        aria-label={t('common:contentLanguages.remove', { language: label })}
                        className="rounded opacity-60 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                )
              })}
              <input
                ref={inputRef}
                type="text"
                role="combobox"
                aria-label={t('common:contentLanguages.title')}
                aria-expanded={showList}
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={showList && suggestions[active] ? `${baseId}-opt-${active}` : undefined}
                value={query}
                onChange={(event) => { setQuery(event.target.value); setOpen(true) }}
                onFocus={() => setOpen(true)}
                onBlur={() => window.setTimeout(() => setOpen(false), 120)}
                onKeyDown={onKeyDown}
                disabled={isDisabled}
                placeholder={value.length === 0 ? t('common:contentLanguages.searchPlaceholder') : ''}
                className="min-w-[8rem] flex-1 bg-transparent px-1 py-1 text-sm outline-none"
                data-languages-input
              />
            </div>

            {showList && (
              <ul
                ref={listRef}
                id={listId}
                role="listbox"
                aria-label={t('common:contentLanguages.title')}
                className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded-lg border bg-surface py-1 shadow-lg"
                data-languages-suggestions
              >
                {suggestions.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-muted-foreground" role="presentation">{t('common:contentLanguages.noMatch')}</li>
                ) : suggestions.map((option, index) => (
                  <li
                    key={option.code}
                    id={`${baseId}-opt-${index}`}
                    role="option"
                    aria-selected={index === active}
                    data-index={index}
                    data-language-option={option.code}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => add(option)}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm',
                      index === active ? 'bg-primary-50 text-primary-700' : 'text-foreground',
                      option.isFamily ? 'font-medium' : 'pl-6'
                    )}
                  >
                    <span>{option.label}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{t('common:contentLanguages.hintSome')}</p>
        </>
      )}
    </div>
  )
}
